/** @file Option group model for Harmony Select components Used to organize options into labeled groups */

import { HxOption } from './option.model';

/**
 * Represents a group of related options
 *
 * @template T The type of the option values in this group
 */
export type HxOptionGroup<T = any> = {
  /** Unique identifier for the group */
  id: string | number;

  /** Display label for the group */
  label: string;

  /** Array of options belonging to this group */
  options: HxOption<T>[];

  /**
   * Whether all options in this group are disabled
   *
   * @default false
   */
  disabled?: boolean;

  /** Optional CSS classes for custom group styling */
  className?: string;
};

/** Type guard to check if an object is a valid HxOptionGroup */
export function isHxOptionGroup<T>(obj: any): obj is HxOptionGroup<T> {
  return (
    obj && typeof obj === 'object' && 'id' in obj && 'label' in obj && 'options' in obj && Array.isArray(obj.options)
  );
}

/** Helper function to create a typed option group */
export function createOptionGroup<T>(
  id: string | number,
  label: string,
  options: HxOption<T>[],
  groupOptions?: Partial<Omit<HxOptionGroup<T>, 'id' | 'label' | 'options'>>
): HxOptionGroup<T> {
  return {
    id,
    label,
    options,
    ...groupOptions,
  };
}

/** Helper to flatten grouped options into a single array Useful for processing all options regardless of grouping */
export function flattenOptionGroups<T>(groups: HxOptionGroup<T>[]): HxOption<T>[] {
  return groups.reduce((acc, group) => {
    return acc.concat(
      group.options.map((opt) => ({
        ...opt,
        groupId: group.id,
      }))
    );
  }, [] as HxOption<T>[]);
}
